import apiClient from "@/services/api/apiClient"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

/** The org's side of the supply chain. */
export type BusinessRole = "manufacturer" | "distributor" | "both"

/** Wire shape returned from GET /organization/. apiClient auto-camelCases
 *  the response so backend snake_case becomes these keys. */
export interface Organization {
  id: string
  name: string
  addressMultiline: string | null
  /** null = not set; the assistant then works it out from the org's products. */
  businessRole: BusinessRole | null
  logoFileKey: string | null
  logoUrl: string | null
  isActive: boolean
  enrichmentText: string | null
  createdAt: Date
  updatedAt: Date
  updatedBy: string
}

/** Body for PUT /organization/{id} — everything the tenant is allowed to
 *  edit through the settings form. Logo goes through its own
 *  upload endpoint (POST /organization/logo). */
export interface OrganizationUpsert {
  name: string
  addressMultiline?: string | null
  /** Left out = unchanged, null = clear. */
  businessRole?: BusinessRole | null
}

const QUERY_KEY = ["organization"] as const

export function useOrganization() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<Organization>("/organization/")
      return response.data
    },
  })
}

export function useUpdateOrganization() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string
      body: OrganizationUpsert
    }) => {
      const response = await apiClient.put<Organization>(
        `/organization/${id}`,
        body,
      )
      return response.data
    },
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data)
    },
  })
}

/** Read a file's bytes as base64 (without the `data:<mime>;base64,` prefix).
 *  FileReader rather than btoa: btoa operates on a binary string, so a UTF-8
 *  SVG would be corrupted before it ever left the browser.
 *
 *  Exported for reuse by other org-scoped upload flows that also send base64
 *  JSON bodies (e.g. `organizationSignaturesApi.ts`) instead of writing a
 *  second copy of this. */
export function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => {
      const result = String(reader.result)
      resolve(result.slice(result.indexOf(",") + 1))
    }
    reader.readAsDataURL(file)
  })
}

/** Upload / replace the letterhead logo. PNG/JPEG/SVG, ≤ 2 MB — the backend
 *  422s with a specific `code` if either is violated. Response is the full
 *  refreshed Organization row so caller can update local state without a
 *  second GET.
 *
 *  Sent as base64 JSON, not multipart: an SVG's raw markup in the request body
 *  matches the WAF's XSS signature, so those uploads were rejected at the edge
 *  with a 403 and never reached the API. */
export function useUploadOrgLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const response = await apiClient.post<Organization>(
        "/organization/logo",
        { contentType: file.type, dataBase64: await readAsBase64(file) },
      )
      return response.data
    },
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data)
    },
  })
}

export function useDeleteOrgLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response =
        await apiClient.delete<Organization>("/organization/logo")
      return response.data
    },
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data)
    },
  })
}
